import React, { useEffect, useState } from 'react';
import InputMask from 'react-input-mask';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';
import ErpTemplate from '../../components/ErpTemplate';
import { FinField, FinInput, FinTextarea } from '../../components/FinField';
import ReactSelect from '../../components/ReactSelect';
import api from '../../services/api';
import { buscarCep, extract } from '../../util/util';
import '../../css/financeiro-parcela-pagar.css';

const ESTADOS = [
    'AC', 'AL', 'AP', 'AM', 'BA', 'CE', 'DF', 'ES', 'GO', 'MA', 'MT', 'MS',
    'MG', 'PA', 'PB', 'PR', 'PE', 'PI', 'RJ', 'RN', 'RS', 'RO', 'RR', 'SC',
    'SP', 'SE', 'TO'
].map((uf) => ({ value: uf, label: uf }));

const TIPOS_CONTA = [
    { value: 'corrente', label: 'Corrente' },
    { value: 'poupanca', label: 'Poupança' },
    { value: 'salario', label: 'Salário' },
];

const TAMANHOS_CAMISA = [
    { value: 'P', label: 'P' },
    { value: 'M', label: 'M' },
    { value: 'G', label: 'G' },
];

const OPCOES_SITUACAO = [
    { value: 'ativo', label: 'Ativo' },
    { value: 'inativo', label: 'Inativo' },
];

const criarDadosIniciais = () => ({
    tipoCadastro: 'freelancer',
    situacaoRegistro: 'ativo',
    enderecopais: '30',
    nomeEnderecopais: 'Brasil',
    enderecouf: 'SP',
    especialidades: [],
});

export default function CadastroFreelancer({ match }) {
    const id = match.params.id;
    const token = localStorage.getItem('token');
    const history = useHistory();
    const [dados, setDados] = useState(criarDadosIniciais());
    const [especialidadesDisponiveis, setEspecialidadesDisponiveis] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isLoading, setIsLoading] = useState(!!id);

    const preencherCep = (cep, dadosAtuais) => {
        buscarCep(cep).then((resposta) => {
            if (resposta.erro) {
                return;
            }
            setDados({
                ...dadosAtuais,
                enderecobairro: resposta.bairro,
                enderecocomplemento: resposta.complemento,
                enderecologradouro: resposta.logradouro,
                enderecopais: '30',
                nomeEnderecopais: 'Brasil',
                enderecomunicipio: resposta.ibge,
                nomeEnderecomunicipio: resposta.localidade,
                enderecouf: resposta.uf || dadosAtuais.enderecouf,
            });
        });
    };

    const buscarEspecialidades = () => {
        api({
            method: 'get',
            action: '/freelaEspecialidade',
            token,
        }).then((response) => {
            setEspecialidadesDisponiveis(response.data.data ?? []);
        }).catch(() => {
            toast.error('Não foi possível carregar as especialidades.');
        });
    };

    const buscar = () => {
        setIsLoading(true);
        if (!id) {
            setDados(criarDadosIniciais());
            setIsLoading(false);
            return;
        }

        api({
            method: 'get',
            action: `/participante/${id}`,
            token,
        }).then((response) => {
            const dadosRecebidos = extract({
                jsonLaravel: response.data.data,
                chaves: ['enderecopais', 'enderecomunicipio', 'banco']
            });
            delete dadosRecebidos.tipoparticipante;

            setDados({
                ...criarDadosIniciais(),
                ...dadosRecebidos,
                especialidades: (dadosRecebidos.especialidades ?? [])
                    .map((item) => item.id)
                    .filter(Boolean)
                    .map((item) => String(item)),
                tipoCadastro: dadosRecebidos.tipoCadastro || 'freelancer',
            });
        }).catch(() => {
            toast.error('Não foi possível carregar o freelancer.');
        }).finally(() => setIsLoading(false));
    };

    const alternarEspecialidade = (especialidadeId) => {
        const especialidadeIdString = String(especialidadeId);
        const selecionadas = new Set((dados.especialidades ?? []).map((item) => String(item)));
        if (selecionadas.has(especialidadeIdString)) {
            selecionadas.delete(especialidadeIdString);
        } else {
            selecionadas.add(especialidadeIdString);
        }
        setDados({
            ...dados,
            especialidades: Array.from(selecionadas),
        });
    };

    const validar = () => {
        if (!String(dados.nome ?? '').trim()) {
            toast.error('É necessário preencher o nome completo.');
            document.querySelector('#nome')?.focus();
            return false;
        }
        if (!String(dados.cpf ?? '').trim()) {
            toast.error('É necessário preencher o CPF.');
            document.querySelector('#cpf')?.focus();
            return false;
        }
        if (!String(dados.celular ?? '').trim()) {
            toast.error('É necessário preencher o celular / WhatsApp.');
            document.querySelector('#celular')?.focus();
            return false;
        }
        if (!String(dados.email ?? '').trim()) {
            toast.error('É necessário preencher o e-mail.');
            document.querySelector('#email')?.focus();
            return false;
        }
        return true;
    };

    const salvar = () => {
        if (isSubmitting || !validar()) {
            return;
        }
        setIsSubmitting(true);

        const dadosParaSalvar = { ...dados };
        dadosParaSalvar.especialidades = (dadosParaSalvar.especialidades ?? [])
            .map((item) => item?.id ?? item?.value ?? item)
            .filter(Boolean)
            .map((item) => String(item));

        if (dadosParaSalvar.fornecedorgrupos?.constructor === Array) {
            dadosParaSalvar.fornecedorgrupos = dadosParaSalvar.fornecedorgrupos
                .map((item) => item?.value ?? item?.id ?? item)
                .filter(Boolean);
        }

        dadosParaSalvar.fornecedorgrupo = dadosParaSalvar.fornecedorgrupo?.value
            ?? dadosParaSalvar.fornecedorgrupo?.id
            ?? dadosParaSalvar.fornecedorgrupo
            ?? null;
        dadosParaSalvar.banco = dadosParaSalvar.banco?.value
            ?? dadosParaSalvar.banco?.id
            ?? dadosParaSalvar.banco
            ?? null;
        dadosParaSalvar.enderecomunicipio = dadosParaSalvar.enderecomunicipio?.value
            ?? dadosParaSalvar.enderecomunicipio?.id
            ?? dadosParaSalvar.enderecomunicipio
            ?? null;
        dadosParaSalvar.enderecopais = dadosParaSalvar.enderecopais?.value
            ?? dadosParaSalvar.enderecopais?.id
            ?? dadosParaSalvar.enderecopais
            ?? null;

        delete dadosParaSalvar.evento;
        delete dadosParaSalvar.cliente;
        delete dadosParaSalvar.eventos;
        delete dadosParaSalvar.unidadenegocio;
        delete dadosParaSalvar.bu;
        delete dadosParaSalvar.cargo;
        delete dadosParaSalvar.categoria;
        delete dadosParaSalvar.tipoparticipante;
        delete dadosParaSalvar.aereomunicipioembarque;
        delete dadosParaSalvar.hotelmunicipio;
        delete dadosParaSalvar.imagemassinatura;
        delete dadosParaSalvar.logomarca;
        delete dadosParaSalvar.grupoSituacao;
        // Freelancer nao tem colunas nomeaveis: limpa o que vem herdado do payload de
        // participante. `colunas` (normalizado) sai pelo mesmo motivo que os nomecolunaN saiam.
        delete dadosParaSalvar.colunas;
        Object.keys(dadosParaSalvar)
            .filter(chave => /^nomecoluna\d+$/.test(chave))
            .forEach(chave => { delete dadosParaSalvar[chave]; });

        const payload = {
            ...dadosParaSalvar,
            tipoCadastro: id ? (dadosParaSalvar.tipoCadastro || 'freelancer') : 'freelancer',
        };

        const requisicao = id
            ? { method: 'put', action: `/participante/${id}` }
            : { method: 'post', action: '/participante' };

        api({
            ...requisicao,
            token,
            data: payload,
        }).then(() => {
            toast.success('Freelancer salvo com sucesso.');
            history.push('/freelancer');
        }).catch((error) => {
            toast.error('Erro ao salvar: ' + (error.response?.data?.error ?? error.message ?? ''));
        }).finally(() => {
            setIsSubmitting(false);
        });
    };

    useEffect(() => {
        document.querySelector('#nome')?.focus();
        buscarEspecialidades();
        buscar();
        // eslint-disable-next-line
    }, []);

    return (
        <ErpTemplate processando={isLoading}>
            <div className="main-content container-fluid fin-pp">
                <div className="fin-pp__head">
                    <div>
                        <h1 className="fin-pp__title">Freelancer</h1>
                        <p className="fin-pp__subtitle">{dados.nome || 'Novo freelancer'}</p>
                    </div>
                    <div className="fin-pp__actions">
                        <button type="button" className="fin-pp__btn fin-pp__btn--primary fin-pp__btn--pill" onClick={salvar} disabled={isSubmitting}>
                            {isSubmitting ? 'Salvando...' : 'Salvar'}
                        </button>
                    </div>
                </div>

                <form className="form" noValidate onSubmit={(e) => { salvar(); e.preventDefault(); }}>
                    <div className="fin-pp__card" style={{ padding: '12px 16px', marginBottom: 16 }}>
                        <div className="fin-pp__cphead">
                            <span className="fin-pp__cphead-title">Dados pessoais</span>
                        </div>
                        <div className="fin-pp__filters">
                            <FinField label="Nome Completo" full>
                                <FinInput id="nome" value={dados.nome ?? ''} onChange={(e) => setDados({ ...dados, nome: e.target.value })} />
                            </FinField>
                            <FinField label="CPF">
                                <InputMask mask="999.999.999-99" className="fin-pp__input" style={{ paddingLeft: 14 }} id="cpf" value={dados.cpf ?? ''} onChange={(e) => setDados({ ...dados, cpf: e.target.value })} />
                            </FinField>
                            <FinField label="RG">
                                <FinInput value={dados.rg ?? ''} onChange={(e) => setDados({ ...dados, rg: e.target.value })} />
                            </FinField>
                            <FinField label="Data de Nascimento">
                                <FinInput type="date" value={dados.datanascimento ?? ''} onChange={(e) => setDados({ ...dados, datanascimento: e.target.value })} />
                            </FinField>
                            <FinField label="Tamanho de Camisa">
                                <ReactSelect
                                    value={dados.tamanhocamisa ? { value: dados.tamanhocamisa, label: dados.tamanhocamisa } : null}
                                    onChange={(e) => setDados({ ...dados, tamanhocamisa: e?.value ?? null })}
                                    options={TAMANHOS_CAMISA}
                                />
                            </FinField>
                            <FinField label="Situação">
                                <ReactSelect
                                    value={dados.situacaoRegistro ? OPCOES_SITUACAO.find((item) => item.value === dados.situacaoRegistro) ?? null : null}
                                    onChange={(e) => setDados({ ...dados, situacaoRegistro: e?.value ?? '' })}
                                    options={OPCOES_SITUACAO}
                                />
                            </FinField>
                        </div>
                    </div>

                    <div className="fin-pp__card" style={{ padding: '12px 16px', marginBottom: 16 }}>
                        <div className="fin-pp__cphead">
                            <span className="fin-pp__cphead-title">Contato</span>
                        </div>
                        <div className="fin-pp__filters">
                            <FinField label="Celular / WhatsApp">
                                <InputMask mask="(99) 99999-9999" className="fin-pp__input" style={{ paddingLeft: 14 }} id="celular" value={dados.celular ?? ''} onChange={(e) => setDados({ ...dados, celular: e.target.value })} />
                            </FinField>
                            <FinField label="Telefone Fixo">
                                <InputMask mask="(99) 9999-9999" className="fin-pp__input" style={{ paddingLeft: 14 }} value={dados.telefone2 ?? ''} onChange={(e) => setDados({ ...dados, telefone2: e.target.value })} />
                            </FinField>
                            <FinField label="E-mail" full>
                                <FinInput id="email" type="email" value={dados.email ?? ''} onChange={(e) => setDados({ ...dados, email: e.target.value })} />
                            </FinField>
                        </div>
                    </div>

                    <div className="fin-pp__card" style={{ padding: '12px 16px', marginBottom: 16 }}>
                        <div className="fin-pp__cphead">
                            <span className="fin-pp__cphead-title">Especialidades</span>
                        </div>
                        <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8 }}>
                            {especialidadesDisponiveis.map((especialidade) => {
                                const ativo = (dados.especialidades ?? [])
                                    .map((item) => String(item))
                                    .includes(String(especialidade.id));
                                return (
                                    <button
                                        key={especialidade.id}
                                        type="button"
                                        className={`fin-pp__btn fin-pp__btn--sm ${ativo ? 'fin-pp__btn--primary' : ''}`}
                                        onClick={() => alternarEspecialidade(especialidade.id)}
                                    >
                                        {especialidade.nome}
                                    </button>
                                );
                            })}
                        </div>
                    </div>

                    <div className="fin-pp__card" style={{ padding: '12px 16px', marginBottom: 16 }}>
                        <div className="fin-pp__cphead">
                            <span className="fin-pp__cphead-title">Endereço</span>
                        </div>
                        <div className="fin-pp__filters">
                            <FinField label="CEP">
                                <InputMask
                                    mask="99999-999"
                                    className="fin-pp__input"
                                    style={{ paddingLeft: 14 }}
                                    value={dados.enderecocep ?? ''}
                                    onChange={(e) => {
                                        const proximo = { ...dados, enderecocep: e.target.value };
                                        setDados(proximo);
                                        if ((e.target.value ?? '').replace(/\D/g, '').length === 8) {
                                            preencherCep(e.target.value, proximo);
                                        }
                                    }}
                                />
                            </FinField>
                            <FinField label="Logradouro" grow>
                                <FinInput value={dados.enderecologradouro ?? ''} onChange={(e) => setDados({ ...dados, enderecologradouro: e.target.value })} />
                            </FinField>
                            <FinField label="Número">
                                <FinInput value={dados.endereconumero ?? ''} onChange={(e) => setDados({ ...dados, endereconumero: e.target.value })} />
                            </FinField>
                            <FinField label="Complemento" grow>
                                <FinInput value={dados.enderecocomplemento ?? ''} onChange={(e) => setDados({ ...dados, enderecocomplemento: e.target.value })} />
                            </FinField>
                            <FinField label="Bairro">
                                <FinInput value={dados.enderecobairro ?? ''} onChange={(e) => setDados({ ...dados, enderecobairro: e.target.value })} />
                            </FinField>
                            <FinField label="Cidade">
                                <ReactSelect
                                    key={`municipio-${dados.enderecouf ?? 'todos'}`}
                                    value={dados.enderecomunicipio ? { value: dados.enderecomunicipio, label: dados.nomeEnderecomunicipio } : null}
                                    onChange={(e) => setDados({ ...dados, enderecomunicipio: e?.value ?? null, nomeEnderecomunicipio: e?.label ?? '' })}
                                    loadOptions={(input) => api({
                                        method: 'get',
                                        action: '/municipio',
                                        token,
                                        params: { nome: input, uf: dados.enderecouf ?? null }
                                    }).then((response) => (response.data.data ?? []).map((registro) => ({ value: registro.id, label: `${registro.nome}-${registro.uf}` }))).catch(() => [])}
                                />
                            </FinField>
                            <FinField label="Estado">
                                <ReactSelect
                                    value={dados.enderecouf ? { value: dados.enderecouf, label: dados.enderecouf } : null}
                                    onChange={(e) => setDados({ ...dados, enderecouf: e?.value ?? '' })}
                                    options={ESTADOS}
                                />
                            </FinField>
                        </div>
                    </div>

                    <div className="fin-pp__card" style={{ padding: '12px 16px', marginBottom: 16 }}>
                        <div className="fin-pp__cphead">
                            <span className="fin-pp__cphead-title">Dados para pagamento</span>
                        </div>
                        <div className="fin-pp__filters">
                            <FinField label="Banco">
                                <ReactSelect
                                    value={dados.banco ? { value: dados.banco, label: dados.nomeBanco } : null}
                                    onChange={(e) => setDados({ ...dados, banco: e?.value ?? null, nomeBanco: e?.label ?? '' })}
                                    loadOptions={(input) => api({
                                        method: 'get',
                                        action: '/financeiroBanco',
                                        token,
                                        params: { nome: input }
                                    }).then((response) => (response.data.data ?? []).map((registro) => ({ value: registro.id, label: `${registro.codigo} - ${registro.nome}` }))).catch(() => [])}
                                />
                            </FinField>
                            <FinField label="Tipo de Conta">
                                <ReactSelect
                                    value={dados.tipoconta ? TIPOS_CONTA.find((opcao) => opcao.value === dados.tipoconta) ?? null : null}
                                    onChange={(e) => setDados({ ...dados, tipoconta: e?.value ?? null })}
                                    options={TIPOS_CONTA}
                                />
                            </FinField>
                            <FinField label="Chave PIX" full>
                                <FinInput value={dados.pix ?? ''} onChange={(e) => setDados({ ...dados, pix: e.target.value })} />
                            </FinField>
                            <FinField label="Agência">
                                <FinInput value={dados.bancoagencia ?? ''} onChange={(e) => setDados({ ...dados, bancoagencia: e.target.value })} />
                            </FinField>
                            <FinField label="Dígito Agência">
                                <FinInput value={dados.bancoagenciadigito ?? ''} onChange={(e) => setDados({ ...dados, bancoagenciadigito: e.target.value })} />
                            </FinField>
                            <FinField label="Conta">
                                <FinInput value={dados.bancoconta ?? ''} onChange={(e) => setDados({ ...dados, bancoconta: e.target.value })} />
                            </FinField>
                            <FinField label="Dígito Conta">
                                <FinInput value={dados.bancocontadigito ?? ''} onChange={(e) => setDados({ ...dados, bancocontadigito: e.target.value })} />
                            </FinField>
                        </div>
                    </div>

                    <div className="fin-pp__card" style={{ padding: '12px 16px', marginBottom: 16 }}>
                        <div className="fin-pp__cphead">
                            <span className="fin-pp__cphead-title">Observações</span>
                        </div>
                        <div className="fin-pp__filters">
                            <FinField label="Observações" full>
                                <FinTextarea id="observacao" value={dados.observacao ?? ''} onChange={(e) => setDados({ ...dados, observacao: e.target.value })} />
                            </FinField>
                        </div>
                    </div>
                </form>
            </div>
        </ErpTemplate>
    );
}
